<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { capitalize, formatNumberDisplayByLang } from '@/helpers/formatters'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { AdsButton, AdsIcon } from '@accor/ads-components'
import { IconAccount } from '@accor/icons'
import StatusNightCardIcon from '@/assets/icons/status-night-card.svg'
import ArrowSwitchIcon from '@/assets/icons/arrow-switch.svg'
import IconStarCircle from '@/components/atoms/icons/IconStarCircle.vue'
import bellIcon from '@/assets/icons/bell.svg'
import { useRoute, useRouter } from 'vue-router'
import { useCompanyStore } from '@/stores/company'
import { useNavigation } from '@/composables/useNavigation'
import AllAccorBusinessLogo from '@/assets/images/all-for-business/logoAllAccorBusiness.svg'
import { trackEvent } from '@/services/analytics-service'
import { ALL_ACCOR_BUSINESS_CONTEXT } from '@/constants/gtm4-aab'
import { ACCOUNT_CONTEXT } from '@/constants/gtm4'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const { firstName, productTier, rewardsPoints, balances, hasGuestNotifications } =
  storeToRefs(useUserStore())
const { isAab: isAabRoute, isAuthorizedForAab } = useNavigation()

const companyStore = useCompanyStore()

const tier = computed(() => productTier.value)
const statusPoints = computed(() => balances.value.nbPointsEarnLast12Months)
const currentNights = computed(() => balances.value.currentNightsBalance)
const companyName = computed(() => companyStore.company?.name)
const companyRewardsPoints = computed(() => companyStore.balancePoints)

const displayedRewardPoints = computed(() => {
  if (isAabRoute.value) {
    const points = parseInt(companyRewardsPoints.value, 10)

    if (!companyRewardsPoints.value || Number.isNaN(points))
      return t('aab.generic.rewardPoints.count.empty')

    return t('aab.navigation.menu.rewardPoints.label', {
      n: points
    })
  } else {
    return t('navigation.header.rewardPoints', {
      reward: formatNumberDisplayByLang(rewardsPoints.value)
    })
  }
})

const ctaTitle = computed(() => {
  return isAabRoute.value ? t('navigationBar.switchToB2C') : t('navigationBar.switchToB2B')
})

const switchMode = () => {
  const routeName = isAabRoute.value ? 'dashboard' : 'aab-dashboard'

  const eventTrackingData = {
    pageName: route.meta.pageName ?? '',
    blocInteraction: isAabRoute.value
      ? 'switch to personal account'
      : 'switch to professional account',
    context: isAabRoute.value ? ALL_ACCOR_BUSINESS_CONTEXT : ACCOUNT_CONTEXT
  }

  trackEvent('GTM_AAB_DASHBOARD_NAV_LINK_ACCOUNT_SWITCHER', eventTrackingData)
  router.push({ name: routeName })
}
</script>

<template>
  <div class="user-card">
    <div class="user-card__info">
      <div class="user-card__text">
        <p v-if="isAabRoute" class="user-card__greeting" data-cs-mask>
          {{ companyName }}
        </p>
        <i18n-t v-else keypath="welcome" tag="p" class="user-card__greeting">
          <template #firstname>
            <span data-cs-mask>{{ capitalize(firstName) }}</span>
          </template>
        </i18n-t>

        <p v-if="isAabRoute" class="user-card__status">
          {{ t('aab.navigation.menu.companyDashboard.label') }}
        </p>
        <p v-else class="user-card__status">
          {{ t('navigation.loyalty.tier') }}
          <span
            class="user-card__status-value"
            :class="`user-card__status-value--${tier.toLowerCase()}`"
            >{{ tier }}</span
          >
        </p>
      </div>
      <div class="user-card__notification">
        <AdsIcon
          v-if="isAabRoute"
          :icon="AllAccorBusinessLogo"
          size="lg"
          class="user-card__all-accor-business-logo"
        />

        <AdsButton
          v-else
          variant="secondary"
          size="medium"
          :aria-label="t('navigation.entry.notifcationCenter')"
          @click="router.push({ name: 'dashboard' })"
          icon-only
          circle
          :prepend-icon="{ icon: bellIcon, size: 'xs' }"
        />
        <span v-if="hasGuestNotifications && !isAabRoute" class="user-card__notification-badge" />
      </div>
    </div>
    <div v-if="!isAabRoute" class="user-card__stat">
      <AdsIcon :icon="StatusNightCardIcon" size="sm" fill="none" />
      <i18n-t keypath="navigation.header.statusPointsAndNights" tag="p">
        <template #statusPoints>
          <span class="user-card__stat-value">{{ formatNumberDisplayByLang(statusPoints) }}</span>
        </template>
        <template #nights>
          <span class="user-card__stat-value">{{ formatNumberDisplayByLang(currentNights) }}</span>
        </template>
      </i18n-t>
    </div>
    <hr class="user-card__separator" />
    <div class="user-card__stat">
      <AdsIcon :icon="IconStarCircle" size="sm" fill="none" />
      <p>{{ displayedRewardPoints }}</p>
    </div>
    <div v-if="isAuthorizedForAab" role="button" class="user-card__switch" @click="switchMode">
      {{ ctaTitle }}
      <AdsIcon v-if="isAabRoute" :icon="ArrowSwitchIcon" size="sm" fill="none" />
      <AdsIcon v-else :icon="IconAccount" size="sm" fill="none" />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/variables' as *;

.user-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ads.$ads-spacing-scale-sm;
  padding: ads.$ads-spacing-scale-lg;
  background-color: ads.$ads-primary-container-low;
  border-radius: ads.$ads-border-radius-container-low;

  &__info {
    display: flex;
    flex-direction: row;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-md;
  }

  &__text {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__notification {
    position: relative;
    margin-left: auto;
  }

  &__all-accor-business-logo {
    background-color: ads.$ads-on-loyalty;
    border-radius: ads.$ads-border-radius-element-md;
  }

  &__notification-badge {
    position: absolute;
    top: -2px;
    right: 0px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: ads.$ads-on-danger-container-low;
    border: 1px solid ads.$ads-surface;
    pointer-events: none;
  }

  &__greeting {
    @include ads-sass.title-03;
    color: ads.$ads-primary;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__status {
    @include ads-sass.body-default-02;
    color: ads.$ads-primary;

    &-value {
      @include ads-sass.body-strong-02;

      &--classic {
        color: ads.$ads-classic;
      }
      &--silver {
        color: ads.$ads-silver;
      }
      &--gold {
        color: ads.$ads-gold;
      }
      &--platinum {
        color: ads.$ads-platinum;
      }
      &--diamond {
        color: ads.$ads-diamond;
      }
      &--limitless {
        color: ads.$ads-limitless;
      }
    }
  }

  &__stat {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-xs;
    @include ads-sass.body-default-02;
    color: ads.$ads-primary;

    &-value {
      @include ads-sass.body-strong-02;
    }
  }

  &__separator {
    width: 100%;
    border: none;
    border-top: 1px solid ads.$ads-loyalty-container-hi;
    margin: 0;
  }

  &__switch {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: stretch;
    gap: ads.$ads-spacing-scale-xs;
    padding-top: ads.$ads-spacing-scale-sm;
    @include ads-sass.link-alternative-01;
    color: ads.$ads-link;
    cursor: pointer;
  }
}
</style>
